import {
  Building2,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Database,
  Archive,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { startTransition, useDeferredValue, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getCanonicalClient,
  createCanonicalClient,
  editCanonicalClient,
  listCanonicalClients,
  transitionCanonicalClient,
  ClientManagementApiError,
  type ClientCoreInput,
  type CanonicalClientListResult,
  type CanonicalClientRecord,
} from "../../lib/arRecoveryClientManagementApi";
import type { ARRecoveryWorkspace } from "../../lib/arRecoveryData";
import { formatDate } from "../../lib/format";
import { ARRecoveryClientProfileSection } from "./ARRecoveryClientProfileSection";

type LoadPhase = "loading" | "ready" | "empty" | "error";
const PAGE_SIZES = new Set([25, 50, 100]);
const STATUS_OPTIONS = ["", "ACTIVE", "INACTIVE", "ARCHIVED"];

const EMPTY_LIST: CanonicalClientListResult = {
  companyId: "",
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 1,
  items: [],
  capabilities: { create: false, edit: false, status: false, archive: false },
};

type TaskMode = "create" | "edit" | null;
const EMPTY_DRAFT: ClientCoreInput = {
  legalName: "", displayName: "", clientType: "RECOVERY_CLIENT", defaultCurrency: "USD",
  timeZone: "America/New_York", paymentReportingEnabled: true, clientPortalEnabled: false,
  statementServicesEnabled: true, recoveryServicesEnabled: true, externalSystemReference: "",
};

export function ARRecoveryClientManagementSection({ workspace: _workspace }: { workspace: ARRecoveryWorkspace }) {
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState<CanonicalClientListResult>(EMPTY_LIST);
  const [selected, setSelected] = useState<CanonicalClientRecord | null>(null);
  const [phase, setPhase] = useState<LoadPhase>("loading");
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const [task, setTask] = useState<TaskMode>(null);
  const [taskStep, setTaskStep] = useState(0);
  const [draft, setDraft] = useState<ClientCoreInput>(EMPTY_DRAFT);
  const [taskError, setTaskError] = useState("");
  const [taskDetails, setTaskDetails] = useState<unknown>();
  const [saving, setSaving] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const listGeneration = useRef(0);
  const detailGeneration = useRef(0);

  const search = params.get("clientSearch") || "";
  const deferredSearch = useDeferredValue(search);
  const status = STATUS_OPTIONS.includes(params.get("clientStatus") || "") ? params.get("clientStatus") || "" : "";
  const sort = (["name", "number", "status", "created", "updated"].includes(params.get("clientSort") || "")
    ? params.get("clientSort")
    : "name") as "name" | "number" | "status" | "created" | "updated";
  const direction = params.get("clientDirection") === "desc" ? "desc" : "asc";
  const parsedPage = Number(params.get("clientPage") || 1);
  const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const parsedPageSize = Number(params.get("clientPageSize") || 25);
  const pageSize = (PAGE_SIZES.has(parsedPageSize) ? parsedPageSize : 25) as 25 | 50 | 100;
  const clientId = params.get("clientId") || "";

  function beginCreate() {
    setDraft(EMPTY_DRAFT);
    setIdempotencyKey(crypto.randomUUID());
    setTask("create"); setTaskStep(0); setTaskError(""); setTaskDetails(undefined);
  }

  function beginEdit() {
    if (!selected) return;
    setDraft({
      legalName: selected.legalName, displayName: selected.displayName, clientType: "RECOVERY_CLIENT",
      defaultCurrency: selected.currency, timeZone: selected.timezone,
      paymentReportingEnabled: selected.paymentReportingEnabled, clientPortalEnabled: selected.clientPortalEnabled,
      statementServicesEnabled: selected.statementServicesEnabled, recoveryServicesEnabled: selected.recoveryServicesEnabled,
      externalSystemReference: selected.externalReference || "",
    });
    setTask("edit"); setTaskStep(0); setTaskError(""); setTaskDetails(undefined);
  }

  function cancelTask() { setTask(null); setTaskError(""); setTaskDetails(undefined); }

  async function saveTask() {
    if (!task) return;
    setSaving(true); setTaskError(""); setTaskDetails(undefined);
    try {
      const saved = task === "create"
        ? await createCanonicalClient(draft, idempotencyKey)
        : await editCanonicalClient(selected!.clientId, { ...draft, expectedVersion: selected!.version });
      const nextId = task === "create" ? saved.clientId : selected!.clientId;
      updateParams({ clientId: nextId });
      setTask(null);
      setRetryToken((value) => value + 1);
    } catch (cause) {
      setTaskError(cause instanceof Error ? cause.message : "Client could not be saved.");
      if (cause instanceof ClientManagementApiError) setTaskDetails(cause.details);
    } finally { setSaving(false); }
  }

  async function changeStatus(nextStatus: "ACTIVE" | "INACTIVE" | "ARCHIVED") {
    if (!selected) return;
    if (nextStatus === "ARCHIVED" && !window.confirm("Archive this client? Existing records will be preserved and operational blockers will be checked.")) return;
    setTaskError(""); setTaskDetails(undefined); setSaving(true);
    try {
      await transitionCanonicalClient(selected.clientId, nextStatus, selected.version);
      setRetryToken((value) => value + 1);
    } catch (cause) {
      setTaskError(cause instanceof Error ? cause.message : "Client status could not be changed.");
      if (cause instanceof ClientManagementApiError) setTaskDetails(cause.details);
    } finally { setSaving(false); }
  }

  function updateParams(changes: Record<string, string | number | null>, replace = false) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    }
    startTransition(() => setParams(next, { replace }));
  }

  useEffect(() => {
    const generation = ++listGeneration.current;
    setPhase("loading");
    setError("");
    listCanonicalClients({ page, pageSize, search: deferredSearch, status, sort, direction })
      .then((next) => {
        if (generation !== listGeneration.current) return;
        const tenantChanged = Boolean(result.companyId && result.companyId !== next.companyId);
        if (tenantChanged) {
          setSelected(null);
          setDetailError("");
          updateParams({ clientId: null }, true);
        }
        setResult(next);
        setPhase(next.total ? "ready" : "empty");
        if (!clientId && next.items[0]) updateParams({ clientId: next.items[0].clientId }, true);
      })
      .catch((cause) => {
        if (generation !== listGeneration.current) return;
        setResult((current) => ({ ...current, items: [], total: 0 }));
        setSelected(null);
        setError(cause instanceof Error ? cause.message : "Client Management could not be loaded.");
        setPhase("error");
      });
  }, [deferredSearch, direction, page, pageSize, retryToken, sort, status]);

  useEffect(() => {
    const generation = ++detailGeneration.current;
    setSelected(null);
    setDetailError("");
    if (!clientId) return;
    setDetailLoading(true);
    getCanonicalClient(clientId, status === "ARCHIVED")
      .then((response) => {
        if (generation !== detailGeneration.current) return;
        if (result.companyId && response.companyId !== result.companyId) {
          setDetailError("The selected client is unavailable in the active company.");
          return;
        }
        setSelected(response.client);
      })
      .catch((cause) => {
        if (generation !== detailGeneration.current) return;
        setDetailError(cause instanceof Error ? cause.message : "Client detail could not be loaded.");
      })
      .finally(() => {
        if (generation === detailGeneration.current) setDetailLoading(false);
      });
  }, [clientId, retryToken, status]);

  const activeCount = result.items.filter((client) => client.status === "ACTIVE").length;
  const portalCount = result.items.filter((client) => client.clientPortalEnabled).length;
  const recoveryCount = result.items.filter((client) => client.recoveryServicesEnabled).length;

  return (
    <div className="ar-workspace-os grid min-w-0 gap-5">
      <section className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#0F766E]">Client Management</p>
          <h2 className="mt-2 font-display text-2xl font-bold leading-tight text-[#0A2540] sm:text-3xl">Canonical client registry</h2>
          <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-[#47606F]">Create and govern tenant-owned recovery clients with canonical identity, concurrency, audit, and lifecycle controls.</p></div>
          {!task && result.capabilities.create ? <button type="button" onClick={beginCreate} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#0A2540] px-4 text-sm font-black text-white"><Plus className="size-4" />Create client</button> : null}
        </div>
      </section>

      {task ? <CoreClientTask mode={task} step={taskStep} setStep={setTaskStep} draft={draft} setDraft={setDraft} error={taskError} saving={saving} onCancel={cancelTask} onSave={saveTask} /> : null}

      {!task ? <><section className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Canonical Client Management summary">
        <Metric icon={Building2} label="Matching clients" value={result.total} note="Tenant-scoped operational records." />
        <Metric icon={ShieldCheck} label="Active on this page" value={activeCount} note="Canonical ACTIVE status only." />
        <Metric icon={Database} label="Portal enabled" value={portalCount} note="Current page service configuration." />
        <Metric icon={CalendarClock} label="Recovery enabled" value={recoveryCount} note="Current page recovery eligibility." />
      </section>

      <section className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]" aria-label="Client directory controls">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_8rem]">
          <label className="flex min-h-11 min-w-0 items-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 focus-within:border-[#14B8A6]">
            <Search className="size-4 shrink-0 text-[#14B8A6]" aria-hidden="true" />
            <span className="sr-only">Search canonical clients</span>
            <input
              value={search}
              onChange={(event) => updateParams({ clientSearch: event.target.value, clientPage: 1 }, true)}
              placeholder="Search number or client name"
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#0A2540] outline-none placeholder:text-[#6A7F8F]"
            />
          </label>
          <Select label="Status" value={status} onChange={(value) => updateParams({ clientStatus: value, clientPage: 1, clientId: null })}>
            <option value="">Operational statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
          </Select>
          <Select label="Sort" value={`${sort}:${direction}`} onChange={(value) => {
            const [nextSort, nextDirection] = value.split(":");
            updateParams({ clientSort: nextSort, clientDirection: nextDirection, clientPage: 1 });
          }}>
            <option value="name:asc">Name A-Z</option>
            <option value="name:desc">Name Z-A</option>
            <option value="number:asc">Client number</option>
            <option value="status:asc">Status</option>
            <option value="updated:desc">Recently updated</option>
          </Select>
          <Select label="Page size" value={String(pageSize)} onChange={(value) => updateParams({ clientPageSize: value, clientPage: 1 })}>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
        </div>
      </section>

      {phase === "loading" ? <LoadingState label="Loading canonical client records..." /> : null}
      {phase === "error" ? <ErrorState message={error} onRetry={() => setRetryToken((value) => value + 1)} /> : null}
      {phase === "empty" ? <EmptyState filtered={Boolean(search || status)} onClear={() => updateParams({ clientSearch: null, clientStatus: null, clientPage: 1, clientId: null })} /> : null}

      {taskError && !task ? <ErrorState message={`${taskError}${formatBlockers(taskDetails)}`} onRetry={() => selected && setRetryToken((value) => value + 1)} compact /> : null}
      {phase === "ready" ? (
        <div className="grid min-w-0 items-start gap-6 xl:grid-cols-[minmax(18rem,.36fr)_minmax(0,.64fr)]">
          <aside className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_18px_38px_rgba(10,37,64,.08)]" aria-label="Canonical client directory">
            <div className="grid gap-3">
              {result.items.map((client) => (
                <button
                  key={client.clientId}
                  type="button"
                  onClick={() => updateParams({ clientId: client.clientId })}
                  aria-current={client.clientId === clientId ? "true" : undefined}
                  className={`min-w-0 rounded-2xl border p-4 text-left transition focus-ring ${client.clientId === clientId ? "border-[#14B8A6] bg-[#E6FFFB]" : "border-[#BFEAFF] bg-[#F4FBFF] hover:bg-white"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-base font-black text-[#0A2540]">{client.displayName}</p>
                      <p className="mt-1 break-words text-xs font-bold text-[#47606F]">{client.clientNumber}</p>
                    </div>
                    <StatusPill status={client.status} />
                  </div>
                  <p className="mt-3 text-xs font-semibold text-[#47606F]">{client.currency} · {client.timezone}</p>
                </button>
              ))}
            </div>
            <Pagination page={result.page} totalPages={result.totalPages} onPage={(nextPage) => updateParams({ clientPage: nextPage, clientId: null })} />
          </aside>

          <main className="min-w-0" aria-live="polite">
            {detailLoading ? <LoadingState label="Loading canonical client detail..." compact /> : null}
            {!detailLoading && detailError ? <ErrorState message={detailError} onRetry={() => setRetryToken((value) => value + 1)} compact /> : null}
            {!detailLoading && !detailError && selected ? <ClientDetail client={selected} capabilities={result.capabilities} saving={saving} onEdit={beginEdit} onStatus={changeStatus} /> : null}
          </main>
        </div>
      ) : null}</> : null}
    </div>
  );
}

function ClientDetail({ client, capabilities, saving, onEdit, onStatus }: { client: CanonicalClientRecord; capabilities: CanonicalClientListResult["capabilities"]; saving: boolean; onEdit: () => void; onStatus: (status: "ACTIVE" | "INACTIVE" | "ARCHIVED") => void }) {
  return (
    <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] pb-5">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#0F766E]">Canonical Client</p>
          <h3 className="mt-2 break-words font-display text-2xl font-bold text-[#0A2540]">{client.displayName}</h3>
          <p className="mt-1 break-words text-sm font-semibold text-[#47606F]">{client.clientNumber} · Version {client.version}</p>
        </div>
        <StatusPill status={client.status} />
      </div>

      {(capabilities.edit || capabilities.status || capabilities.archive) ? <div className="mt-4 flex flex-wrap gap-2" aria-label="Client actions">
        {capabilities.edit ? <button type="button" onClick={onEdit} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-[#14B8A6] px-3 text-sm font-black text-[#0F766E]"><Pencil className="size-4" />Edit client</button> : null}
        {capabilities.status && client.status === "INACTIVE" ? <button disabled={saving} type="button" onClick={() => onStatus("ACTIVE")} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-[#0F766E] px-3 text-sm font-black text-white"><Power className="size-4" />Activate</button> : null}
        {capabilities.status && client.status === "ACTIVE" ? <button disabled={saving} type="button" onClick={() => onStatus("INACTIVE")} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-amber-500 px-3 text-sm font-black text-amber-800"><Power className="size-4" />Deactivate</button> : null}
        {capabilities.archive && client.status !== "ARCHIVED" ? <button disabled={saving} type="button" onClick={() => onStatus("ARCHIVED")} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-red-300 px-3 text-sm font-black text-red-800"><Archive className="size-4" />Archive</button> : null}
      </div> : null}

      <section className="mt-5">
        <h4 className="text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">Available canonical data</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Fact label="Legal name" value={client.legalName} />
          <Fact label="Client number" value={client.clientNumber} />
          <Fact label="Currency" value={client.currency} />
          <Fact label="Operational timezone" value={client.timezone} />
          <Fact label="Data quality" value={client.dataQualityStatus} />
          <Fact label="External reference" value={client.externalReference || "Not configured"} />
          <Fact label="Client Portal" value={yesNo(client.clientPortalEnabled)} />
          <Fact label="Recovery services" value={yesNo(client.recoveryServicesEnabled)} />
          <Fact label="Statement services" value={yesNo(client.statementServicesEnabled)} />
          <Fact label="Payment reporting" value={yesNo(client.paymentReportingEnabled)} />
          <Fact label="Created" value={formatDate(client.createdAt)} />
          <Fact label="Updated" value={formatDate(client.updatedAt)} />
        </div>
      </section>

      <ARRecoveryClientProfileSection clientId={client.clientId} />

    </article>
  );
}

function CoreClientTask({ mode, step, setStep, draft, setDraft, error, saving, onCancel, onSave }: { mode: Exclude<TaskMode, null>; step: number; setStep: (step: number) => void; draft: ClientCoreInput; setDraft: (draft: ClientCoreInput) => void; error: string; saving: boolean; onCancel: () => void; onSave: () => void }) {
  const steps = ["Client Identity", "Operating Settings", "Core Services", "Review & Create"];
  const validIdentity = draft.legalName.trim().length >= 2 && draft.displayName.trim().length >= 2;
  const nextAllowed = step !== 0 || validIdentity;
  const field = <K extends keyof ClientCoreInput>(key: K, value: ClientCoreInput[K]) => setDraft({ ...draft, [key]: value });
  return <section className="min-w-0 rounded-[1.75rem] border border-[#14B8A6] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]" aria-label={`${mode === "create" ? "Create" : "Edit"} canonical client`}>
    <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#0F766E]">{mode === "create" ? "Create client" : "Edit client"}</p>
    <h3 className="mt-2 font-display text-2xl font-bold text-[#0A2540]">{steps[step]}</h3>
    <ol className="mt-4 grid gap-2 sm:grid-cols-4">{steps.map((label, index) => <li key={label} aria-current={index === step ? "step" : undefined} className={`rounded-xl border p-2 text-xs font-black ${index === step ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0F766E]" : "border-[#BFEAFF] text-[#47606F]"}`}>{index + 1}. {label}</li>)}</ol>
    {error ? <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-900">{error}</p> : null}
    <div className="mt-5 grid gap-4 sm:grid-cols-2">
      {step === 0 ? <><TaskInput label="Legal name" value={draft.legalName} required onChange={(value) => field("legalName", value)} /><TaskInput label="Display name" value={draft.displayName} required onChange={(value) => field("displayName", value)} /><TaskInput label="External system reference" value={draft.externalSystemReference} onChange={(value) => field("externalSystemReference", value)} /></> : null}
      {step === 1 ? <><TaskInput label="Currency" value={draft.defaultCurrency} required maxLength={3} onChange={(value) => field("defaultCurrency", value.toUpperCase())} /><TaskInput label="IANA timezone" value={draft.timeZone} required onChange={(value) => field("timeZone", value)} /><Fact label="Initial status" value={mode === "create" ? "INACTIVE - activation is separate" : "Status is changed through governed actions"} /></> : null}
      {step === 2 ? <>{([["recoveryServicesEnabled", "Recovery services"], ["statementServicesEnabled", "Statement services"], ["paymentReportingEnabled", "Payment reporting"], ["clientPortalEnabled", "Client Portal"]] as const).map(([key, label]) => <label key={key} className="flex min-h-14 items-center gap-3 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-black text-[#0A2540]"><input type="checkbox" checked={draft[key]} onChange={(event) => field(key, event.target.checked)} className="size-4 accent-[#14B8A6]" />{label}</label>)}</> : null}
      {step === 3 ? <><Fact label="Legal name" value={draft.legalName} /><Fact label="Display name" value={draft.displayName} /><Fact label="Currency" value={draft.defaultCurrency} /><Fact label="Timezone" value={draft.timeZone} /><Fact label="Recovery" value={yesNo(draft.recoveryServicesEnabled)} /><Fact label="Statements" value={yesNo(draft.statementServicesEnabled)} /><Fact label="Payment reporting" value={yesNo(draft.paymentReportingEnabled)} /><Fact label="Client Portal" value={yesNo(draft.clientPortalEnabled)} /></> : null}
    </div>
    <div className="mt-6 flex flex-wrap justify-between gap-3 border-t border-[#BFEAFF] pt-4"><button type="button" onClick={onCancel} className="min-h-11 rounded-xl border border-[#BFEAFF] px-4 text-sm font-black text-[#0A2540]">Cancel</button><div className="flex gap-2">{step > 0 ? <button type="button" onClick={() => setStep(step - 1)} className="min-h-11 rounded-xl border border-[#14B8A6] px-4 text-sm font-black text-[#0F766E]">Back</button> : null}{step < 3 ? <button type="button" disabled={!nextAllowed} onClick={() => setStep(step + 1)} className="min-h-11 rounded-xl bg-[#0A2540] px-4 text-sm font-black text-white disabled:opacity-40">Continue</button> : <button type="button" disabled={saving} onClick={onSave} className="min-h-11 rounded-xl bg-[#0F766E] px-4 text-sm font-black text-white disabled:opacity-40">{saving ? "Saving..." : mode === "create" ? "Create inactive client" : "Save changes"}</button>}</div></div>
  </section>;
}

function TaskInput({ label, value, onChange, required = false, maxLength }: { label: string; value: string; onChange: (value: string) => void; required?: boolean; maxLength?: number }) { return <label className="min-w-0"><span className="mb-2 block text-xs font-black uppercase tracking-[.1em] text-[#0F766E]">{label}{required ? " *" : ""}</span><input value={value} required={required} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} className="min-h-11 w-full rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-sm font-semibold text-[#0A2540] outline-none focus:border-[#14B8A6]" /></label>; }

function formatBlockers(details: unknown) { const value = details as { blockers?: Record<string, number> } | undefined; if (!value?.blockers) return ""; return ` Blockers: ${Object.entries(value.blockers).filter(([, count]) => count > 0).map(([label, count]) => `${label} (${count})`).join(", ")}.`; }

function Metric({ icon: Icon, label, value, note }: { icon: typeof Building2; label: string; value: number; note: string }) {
  return <article className="rounded-2xl border border-[#BFEAFF] bg-white p-4"><Icon className="size-5 text-[#14B8A6]" aria-hidden="true" /><p className="mt-3 text-2xl font-black text-[#0A2540]">{value}</p><p className="mt-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p><p className="mt-2 text-xs font-semibold text-[#47606F]">{note}</p></article>;
}

function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: ReactNode }) {
  return <label className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</span><select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full min-w-0 bg-transparent text-sm font-bold text-[#0A2540] outline-none">{children}</select></label>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p><p className="mt-1 break-words text-sm font-black text-[#0A2540]" style={{ overflowWrap: "anywhere" }}>{value}</p></div>;
}

function StatusPill({ status }: { status: string }) {
  return <span className="shrink-0 rounded-full border border-[#14B8A6] bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">Status: {status}</span>;
}

function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (page: number) => void }) {
  return <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#BFEAFF] pt-4"><button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} className="inline-flex min-h-10 items-center gap-1 rounded-xl border border-[#BFEAFF] px-3 text-xs font-black text-[#0A2540] disabled:opacity-40"><ChevronLeft className="size-4" />Previous</button><span className="text-xs font-black text-[#47606F]">Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="inline-flex min-h-10 items-center gap-1 rounded-xl border border-[#BFEAFF] px-3 text-xs font-black text-[#0A2540] disabled:opacity-40">Next<ChevronRight className="size-4" /></button></div>;
}

function LoadingState({ label, compact = false }: { label: string; compact?: boolean }) {
  return <div role="status" className={`grid place-items-center rounded-[1.75rem] border border-[#BFEAFF] bg-white p-8 text-center ${compact ? "min-h-64" : "min-h-80"}`}><div><RefreshCw className="mx-auto size-7 animate-spin text-[#14B8A6]" aria-hidden="true" /><p className="mt-3 text-sm font-black text-[#0A2540]">{label}</p></div></div>;
}

function ErrorState({ message, onRetry, compact = false }: { message: string; onRetry: () => void; compact?: boolean }) {
  return <div role="alert" className={`grid place-items-center rounded-[1.75rem] border border-red-200 bg-red-50 p-8 text-center ${compact ? "min-h-64" : "min-h-80"}`}><div><CircleAlert className="mx-auto size-8 text-red-700" aria-hidden="true" /><p className="mt-3 font-black text-red-900">Client Management is unavailable</p><p className="mt-2 max-w-xl text-sm font-semibold text-red-800">{message}</p><button type="button" onClick={onRetry} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#0A2540] px-4 text-sm font-black text-white"><RefreshCw className="size-4" />Retry</button></div></div>;
}

function EmptyState({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  return <div className="grid min-h-80 place-items-center rounded-[1.75rem] border border-dashed border-[#BFEAFF] bg-white p-8 text-center"><div><Building2 className="mx-auto size-9 text-[#14B8A6]" aria-hidden="true" /><p className="mt-3 font-black text-[#0A2540]">{filtered ? "No operational clients match the current filters." : "No operational clients are available for this company."}</p>{filtered ? <button type="button" onClick={onClear} className="mt-5 min-h-11 rounded-xl border border-[#14B8A6] px-4 text-sm font-black text-[#0F766E]">Clear filters</button> : null}</div></div>;
}

function yesNo(value: boolean) { return value ? "Enabled" : "Not enabled"; }
